import { useState, useMemo } from "react";
import { EARNINGS_CALCULATOR_CONSTANTS } from "./EarningsCalculator.constants";
import {
  calculateCommissionPerReferral,
  calculateTotalEarnings,
  formatIndianCurrency,
} from "./EarningsCalculator.utils";

export const useEarningsCalculator = (referralOverview) => {
  const {
    COMMISSION_RATE,
    DEFAULT_CURRENCY_SYMBOL,
    PEOPLE_OPTIONS,
    PLANS,
    BREAKDOWN_COUNTS,
    REFERRAL_SUFFIX_SINGULAR,
    REFERRAL_SUFFIX_PLURAL,
  } = EARNINGS_CALCULATOR_CONSTANTS;

  const [selectedPeopleCount, setSelectedPeopleCount] = useState(
    PEOPLE_OPTIONS[0].VALUE
  );
  const [selectedPlanId, setSelectedPlanId] = useState(PLANS[0].ID);

  const activeRate = referralOverview?.commission_rate ?? COMMISSION_RATE;

  const selectedPlan = useMemo(() => {
    return PLANS.find((p) => p.ID === selectedPlanId) || PLANS[0];
  }, [PLANS, selectedPlanId]);

  const perReferralCommission = useMemo(() => {
    return calculateCommissionPerReferral(
      selectedPlan.ANNUAL_PRICE,
      activeRate
    );
  }, [selectedPlan, activeRate]);

  const totalEarnings = useMemo(() => {
    return calculateTotalEarnings(
      selectedPeopleCount,
      selectedPlan.ANNUAL_PRICE,
      activeRate
    );
  }, [selectedPeopleCount, selectedPlan, activeRate]);

  const breakdownRows = useMemo(() => {
    return BREAKDOWN_COUNTS.map((count) => {
      const earned = calculateTotalEarnings(
        count,
        selectedPlan.ANNUAL_PRICE,
        activeRate
      );
      const label =
        count === 1
          ? `${count} ${REFERRAL_SUFFIX_SINGULAR}`
          : `${count} ${REFERRAL_SUFFIX_PLURAL}`;
      return {
        id: `breakdown-${count}`,
        count,
        label,
        formattedAmount: `${DEFAULT_CURRENCY_SYMBOL}${formatIndianCurrency(
          earned
        )}`,
      };
    });
  }, [
    BREAKDOWN_COUNTS,
    selectedPlan,
    activeRate,
    DEFAULT_CURRENCY_SYMBOL,
    REFERRAL_SUFFIX_SINGULAR,
    REFERRAL_SUFFIX_PLURAL,
  ]);

  return {
    selectedPeopleCount,
    setSelectedPeopleCount,
    selectedPlanId,
    setSelectedPlanId,
    selectedPlan,
    activeRate,
    perReferralCommission,
    totalEarnings,
    formattedTotalEarnings: `${DEFAULT_CURRENCY_SYMBOL}${formatIndianCurrency(
      totalEarnings
    )}`,
    formattedPerReferral: `${DEFAULT_CURRENCY_SYMBOL}${formatIndianCurrency(
      perReferralCommission
    )}`,
    formattedPlanPrice: `${DEFAULT_CURRENCY_SYMBOL}${formatIndianCurrency(
      selectedPlan.ANNUAL_PRICE
    )}`,
    breakdownRows,
  };
};
